import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronLeft, Loader2 } from 'lucide-react'
import { supabase } from '../lib/supabase'
import Toast from '../components/Toast'
import { useToast } from '../hooks/useToast'

const COUNTRIES = [
  { code: '+91',  flag: '🇮🇳', name: 'India' },
  { code: '+1',   flag: '🇺🇸', name: 'USA' },
  { code: '+44',  flag: '🇬🇧', name: 'UK' },
  { code: '+61',  flag: '🇦🇺', name: 'Australia' },
  { code: '+971', flag: '🇦🇪', name: 'UAE' },
  { code: '+65',  flag: '🇸🇬', name: 'Singapore' },
  { code: '+60',  flag: '🇲🇾', name: 'Malaysia' },
]

function detectCountry() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
    if (tz.startsWith('Asia/Kolkata') || tz.startsWith('Asia/Calcutta')) return COUNTRIES[0]
    if (tz.startsWith('America/')) return COUNTRIES[1]
    if (tz.startsWith('Europe/London')) return COUNTRIES[2]
    if (tz.startsWith('Australia/')) return COUNTRIES[3]
    if (tz.startsWith('Asia/Dubai')) return COUNTRIES[4]
  } catch {}
  return COUNTRIES[0]
}

const TEST_PHONE = '9999999999'
const TEST_OTP   = '000000'

export default function Login() {
  const navigate = useNavigate()
  const { toast, showToast, dismissToast } = useToast()

  const [countryCode, setCountryCode] = useState(() => detectCountry())
  const [phone, setPhone] = useState('')
  const [otpSent, setOtpSent] = useState(false)
  const [otp, setOtp] = useState('')
  const [loading, setLoading] = useState(false)

  const isTestPhone = phone === TEST_PHONE && countryCode.code === '+91'

  async function handleSendOtp(e) {
    e.preventDefault()
    if (phone.length < 10) { showToast('Enter a valid phone number', 'error'); return }

    if (isTestPhone) {
      setOtpSent(true)
      showToast(`Test mode — use OTP ${TEST_OTP}`)
      return
    }

    setLoading(true)
    const { data, error } = await supabase.functions.invoke('send-otp', {
      body: { phone: `${countryCode.code}${phone}` },
    })
    setLoading(false)

    if (error || !data?.ok) {
      showToast(data?.error || 'Failed to send OTP', 'error')
    } else {
      setOtpSent(true)
      showToast(`OTP sent to ${countryCode.code} ${phone}`)
    }
  }

  async function handleVerifyOtp(e) {
    e.preventDefault()
    if (otp.length < 4) { showToast('Enter the OTP you received', 'error'); return }

    if (isTestPhone) {
      if (otp !== TEST_OTP) { showToast(`Use OTP ${TEST_OTP} for test number`, 'error'); return }
      showToast('Test mode — OTP accepted')
      navigate('/dashboard')
      return
    }

    setLoading(true)
    const { data, error } = await supabase.functions.invoke('verify-otp', {
      body: { phone: `${countryCode.code}${phone}`, code: otp },
    })
    setLoading(false)

    if (error || !data?.ok) {
      showToast(data?.error || 'Invalid OTP', 'error')
      return
    }

    await supabase.auth.setSession({
      access_token: data.access_token,
      refresh_token: data.refresh_token,
    })

    // New user with no profile → send to onboarding
    const { data: profile } = await supabase
      .from('profiles')
      .select('id')
      .eq('id', data.user_id)
      .maybeSingle()

    if (!profile) {
      navigate('/onboarding')
    } else {
      navigate('/dashboard')
    }
  }

  return (
    <div className="min-h-screen bg-bg text-brand-text font-sans">
      <Toast toast={toast} onDismiss={dismissToast} />

      <div className="mx-auto max-w-[480px] px-5 py-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-10">
          <button
            onClick={() => navigate('/')}
            className="p-2 rounded-xl text-brand-muted hover:text-brand-text transition-colors"
            style={{ background: '#13131a' }}
          >
            <ChevronLeft size={18} />
          </button>
          <span className="font-syne font-extrabold text-xl">
            Park<span className="text-accent">Ping</span>
          </span>
        </div>

        <h2 className="font-syne font-bold text-2xl mb-1">Welcome back</h2>
        <p className="text-sm text-brand-muted mb-8">Sign in to manage your tags and dashboard.</p>

        {!otpSent ? (
          <form onSubmit={handleSendOtp} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-brand-text mb-1.5">Mobile Number</label>
              <div className="flex rounded-2xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.1)' }}>
                <select
                  value={countryCode.code + '|' + countryCode.name}
                  onChange={(e) => {
                    const [code, name] = e.target.value.split('|')
                    setCountryCode(COUNTRIES.find(c => c.code === code && c.name === name) || COUNTRIES[0])
                    setPhone('')
                  }}
                  className="px-2 py-3 text-sm font-semibold text-accent outline-none cursor-pointer"
                  style={{ background: '#1e1e28', border: 'none', borderRight: '1px solid rgba(255,255,255,0.1)' }}
                >
                  {COUNTRIES.map((c) => (
                    <option key={c.code + c.name} value={c.code + '|' + c.name}>
                      {c.flag} {c.code}
                    </option>
                  ))}
                </select>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 15))}
                  placeholder="Phone number"
                  className="flex-1 px-3 py-3 bg-surface text-brand-text placeholder-brand-muted outline-none"
                  autoFocus
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 rounded-2xl font-syne font-bold text-bg flex items-center justify-center gap-2 transition-all hover:opacity-90 active:scale-95 disabled:opacity-60"
              style={{ background: '#00e5a0', boxShadow: '0 0 20px rgba(0,229,160,0.35)' }}
            >
              {loading && <Loader2 size={18} className="animate-spin" />}
              Send OTP
            </button>

            <p className="text-center text-xs text-brand-muted pt-2">
              New here?{' '}
              <button type="button" onClick={() => navigate('/onboarding')}
                className="text-accent font-semibold hover:underline">
                Create a tag
              </button>
            </p>
          </form>
        ) : (
          <form onSubmit={handleVerifyOtp} className="space-y-4">
            <div
              className="flex items-center gap-2 px-4 py-3 rounded-2xl text-sm"
              style={{ background: 'rgba(0,229,160,0.08)', border: '1px solid rgba(0,229,160,0.2)', color: '#00e5a0' }}
            >
              ✓ OTP sent to {countryCode.code} {phone}
            </div>

            <div>
              <label className="block text-sm font-medium text-brand-text mb-1.5">Enter OTP</label>
              <input
                type="text"
                inputMode="numeric"
                value={otp}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, '').slice(0, 6)
                  setOtp(val)
                  if (val.length === 6) setTimeout(() => document.getElementById('login-verify-btn')?.click(), 100)
                }}
                placeholder="6-digit OTP"
                className="w-full px-4 py-3 rounded-2xl text-brand-text text-center text-2xl tracking-[0.5em] placeholder-brand-muted outline-none transition-all"
                style={{ background: '#13131a', border: '1px solid rgba(255,255,255,0.1)' }}
                onFocus={(e) => (e.target.style.borderColor = 'rgba(0,229,160,0.5)')}
                onBlur={(e) => (e.target.style.borderColor = 'rgba(255,255,255,0.1)')}
                autoFocus
                required
              />
            </div>

            <button
              id="login-verify-btn"
              type="submit"
              disabled={loading}
              className="w-full py-4 rounded-2xl font-syne font-bold text-bg flex items-center justify-center gap-2 transition-all hover:opacity-90 active:scale-95 disabled:opacity-60"
              style={{ background: '#00e5a0', boxShadow: '0 0 20px rgba(0,229,160,0.35)' }}
            >
              {loading && <Loader2 size={18} className="animate-spin" />}
              Verify & Go to Dashboard
            </button>

            <button
              type="button"
              onClick={() => { setOtpSent(false); setOtp('') }}
              className="w-full text-sm text-brand-muted hover:text-brand-text transition-colors py-2"
            >
              Change number
            </button>
          </form>
        )}
      </div>
    </div>
  )
}
